import { useQuery, useMutation, UseQueryResult } from '@tanstack/react-query';
import User from '@/types/userType';
import fetchUser, { fetchUserByEmail, searchUsers } from '@/apis/fetchUser';
import headerObject from '@/types/headerObj';
import { getQueryClient } from '@/state/orgState';
import emptyUser from '@/types/emptyUser';
import useUserStore from '@/state/userState';

// Fetch user by UUID
export const useUserByUUID = (
  userUUID: string,
  headerObj: headerObject,
  enabled: boolean = true,
  // Callers that use this result for an authorization decision should pass
  // 0: the default 5-minute staleTime is fine for display purposes, but
  // would otherwise let a target who changed teams in the last 5 minutes
  // still authorize access via a stale cached row.
  staleTime: number = 5 * 60 * 1000
): UseQueryResult<User | undefined> => {
  return useQuery<User | undefined>({
    queryKey: ['user', 'byUUID', userUUID],
    queryFn: async () => {
      const userStore = useUserStore.getState().user;
      if (userStore?.accessToken) {
        headerObj.Authorization = `Bearer ${userStore.accessToken}`;
      }
      if (!userUUID) return emptyUser;
      const user = await fetchUser(userUUID, headerObj);
      return user || emptyUser;
    },
    enabled: !!userUUID && enabled,
    staleTime,
  });
};

// Fetch user by email
export const useUserByEmail = (
  email: string,
  headerObj: headerObject
): UseQueryResult<User | undefined> => {
  return useQuery<User | undefined>({
    queryKey: ['user', 'byEmail', email],
    queryFn: async () => {
      const userStore = useUserStore.getState().user;
      if (userStore?.accessToken) {
        headerObj.Authorization = `Bearer ${userStore.accessToken}`;
      }
      if (!email) return emptyUser;
      const user = await fetchUserByEmail(email, headerObj);
      return user || emptyUser;
    },
    enabled: !!email,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Update user organization mutation
export const useUpdateUserOrganization = () => {
  const queryClient = getQueryClient();

  return useMutation({
    mutationFn: async ({
      user,
      organizationUUID,
    }: {
      user: User;
      organizationUUID: string;
    }) => {
      // This is just updating local state, no API call needed
      return {
        ...user,
        OrganizationUUID: organizationUUID,
      };
    },
    onSuccess: (updatedUser) => {
      // Invalidate user queries
      queryClient.invalidateQueries({
        queryKey: ['user', 'byUUID', updatedUser.UserUUID],
      });
      queryClient.invalidateQueries({
        queryKey: ['user', 'byEmail', updatedUser.Email],
      });
    },
  });
};

// Search users by GitHub or GitLab username
export const useSearchUsers = (
  searchQuery: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<{ users: User[] } | undefined>({
    queryKey: ['users', 'search', searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 3) return undefined;

      // Search by GitHub username first
      let data = await searchUsers({ github_username: searchQuery.trim() });

      // If no results with GitHub, try GitLab
      if (!data || !data.users || data.users.length === 0) {
        const gitlabData = await searchUsers({
          gitlab_username: searchQuery.trim(),
        });
        if (gitlabData && gitlabData.users && gitlabData.users.length > 0) {
          data = gitlabData;
        }
      }

      return data;
    },
    enabled: (options?.enabled ?? true) && searchQuery.length >= 3,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};
